import { Component, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import {
  barcodeValidator,
  extractApiErrorMessage,
  isApiSuccess
} from 'src/app/services/barcode.util';
import { NbDialogRef, NbDialogService } from '@nebular/theme';
import { EcommerceService } from 'src/app/services/ecommerce.service';
import { ImageUrlService } from 'src/app/services/image-url.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Subject, of, throwError } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, finalize, map, switchMap, takeUntil } from 'rxjs/operators';
import { GlobalProductService } from 'src/app/services/global-product.service';
import { GlobalCategoryService } from 'src/app/services/global-category.service';
import { GlobalReferenceService, DskGroupSearchItem, VendorSearchItem } from 'src/app/services/global-reference.service';
import {
  formatDskSearchOptionLabel,
  formatDskSearchOptionMeta,
  patchDskClassification,
  patchVendorFields
} from 'src/app/services/global-reference.util';
import {
  computeLiterPrice,
  formatLiterPriceDisplay,
  isLiquidUnitCode,
  UNIT_CODE_OPTIONS
} from 'src/app/services/unit-pricing.util';
import { ProductFeatureFlagsService } from 'src/app/services/product-feature-flags.service';
import {
  appendIngredientsNutritionFormData,
  EU_ALLERGEN_OPTIONS,
  INGREDIENTS_MAX_LENGTH,
  INGREDIENTS_SOFT_WARN_LENGTH,
  isAllergenSelected,
  NUTRITION_FIELDS,
  patchIngredientsNutrition,
  createNutritionFormGroup,
  toggleAllergenSelection
} from 'src/app/services/product-nutrition.util';
import {
  appendVendorOrderFormData,
  coliValidator,
  patchVendorOrder,
  shelfLifeDaysValidator
} from 'src/app/services/product-vendor-order.util';
import {
  buildPlacementPayload,
  createPlacementFormControls,
  hasPlacementInput,
  patchPlacement,
  PlacementFormValues,
  placementValuesEqual,
  snapshotPlacementValues
} from 'src/app/services/product-placement.util';
import { PlacementHistoryDialogComponent } from '../placement-history-dialog/placement-history-dialog.component';
import { PriceHistoryDialogComponent } from '../price-history-dialog/price-history-dialog.component';
import {
  datetimeLocalToIso,
  formatMultiBuyOfferSummary,
  formatPricingDate,
  futureDateTimeValidator,
  getMultiBuyOfferStatus,
  isoToDatetimeLocal,
  isDuplicateActivatesAtError,
  MultiBuyOfferStatusInfo,
  offerPriceValidator,
  requiredQuantityValidator,
  scheduledPriceValidator,
  validateScheduleDeactivatesAt
} from 'src/app/services/product-pricing.util';
import Swal from 'sweetalert2';
@Component({
  selector: 'app-product-form-dialog',
  templateUrl: './product-form-dialog.component.html',
  styleUrls: ['./product-form-dialog.component.scss']
})
export class ProductFormDialogComponent implements OnInit, OnDestroy {

  @Input() storeId: string = '';
  @Input() product: any = null;
  @Input() categories: any[] = [];
  @Input() storeCurrency: string = 'DKK';
  @Input() skuRequirementEnabled: boolean = false;
  @Input() depositSettings: any = {
    enabled: false,
    categoryA: 1.00,
    categoryB: 1.50,
    categoryC: 3.00
  };
  @Input() depositFeeTypes: any[] = [];
  @Input() autoAssignByEAN: boolean = false;
  @Input() catalogImportDefaultCategoryId: string = '';

  eanAutoDetectHint: string = '';
  detectingEAN: boolean = false;

  // Global Product Linking
  globalSearchTerm = '';
  globalSearchResults: any[] = [];
  globalSearchLoading = false;
  selectedGlobalProduct: any = null;
  globalLinkLoading = false;
  globalPanelOpen = false;
  pendingGlobalProductLink: any = null;
  catalogImportCategoryHint: string = '';
  fieldLocks: Record<string, boolean> = {};
  fieldLocksSaving = false;
  private globalSearchSubject = new Subject<string>();

  readonly lockableFields = [
    { key: 'name', label: 'Name' },
    { key: 'description', label: 'Description' },
    { key: 'barcode', label: 'Barcode' },
    { key: 'sku', label: 'SKU' },
    { key: 'price', label: 'Price' },
    { key: 'costPrice', label: 'Cost price' },
    { key: 'images', label: 'Images' },
    { key: 'weight', label: 'Weight' },
    { key: 'dimensions', label: 'Dimensions' },
    { key: 'ageRestricted', label: 'Age Restricted' },
    { key: 'minimumAge', label: 'Minimum Age' },
    { key: 'hasDeposit', label: 'Has Deposit' },
    { key: 'depositCategory', label: 'Deposit Category' },
    { key: 'depositFeeTypeId', label: 'Deposit Fee Type' },
    { key: 'tags', label: 'Tags' },
    { key: 'mainGroup', label: 'Main Group' },
    { key: 'productGroup', label: 'Product Group' },
    { key: 'subGroup', label: 'Sub Group' },
    { key: 'dskAssortmentID', label: 'DSK Assortment ID' },
    { key: 'family', label: 'Family' },
    { key: 'discountGroup', label: 'Discount Group' },
    { key: 'vendorID', label: 'Vendor ID' },
    { key: 'vendorName', label: 'Vendor Name' }
  ];

  private readonly invalidControlLabels: Record<string, string> = {
    name: 'Product Name',
    categoryId: 'Category',
    price: 'Price',
    barcode: 'Barcode',
    sku: 'SKU',
    quantity: 'Quantity'
  };

  productForm!: FormGroup;
  productImages: File[] = [];
  productImagePreviews: string[] = [];
  primaryImageIndex: number = 0;
  readonly unitCodeOptions = UNIT_CODE_OPTIONS;
  readonly allergenOptions = EU_ALLERGEN_OPTIONS;
  readonly nutritionFields = NUTRITION_FIELDS;
  readonly ingredientsMaxLength = INGREDIENTS_MAX_LENGTH;
  readonly ingredientsSoftWarnLength = INGREDIENTS_SOFT_WARN_LENGTH;
  showAllergens = false;
  showNutrition = false;
  serverLiterPrice: number | null = null;
  useLiveLiterPricePreview = false;
  private savedPlacementSnapshot: PlacementFormValues = snapshotPlacementValues({});

  // Scheduled pricing
  upcomingPriceSchedule: any = null;
  activePriceSchedule: any = null;
  schedulePanelOpen = false;
  schedulePrice: number | null = null;
  scheduleActivatesAt = '';
  scheduleDeactivatesAt = '';
  schedulePriceTouched = false;
  scheduleDateTouched = false;
  scheduleDeactivatesTouched = false;
  scheduleSaving = false;
  scheduleInlineError = '';
  editingUpcomingSchedule = false;
  editSchedulePrice: number | null = null;
  editScheduleActivatesAt = '';
  editScheduleDeactivatesAt = '';
  editSchedulePriceTouched = false;
  editScheduleDateTouched = false;
  editScheduleDeactivatesTouched = false;
  editingActiveScheduleEnd = false;
  activeScheduleDeactivatesAt = '';
  activeScheduleDeactivatesTouched = false;
  activeScheduleEndError = '';

  dskSearchInput$ = new Subject<string>();
  vendorSearchInput$ = new Subject<string>();
  dskSearchResults: DskGroupSearchItem[] = [];
  vendorSearchResults: VendorSearchItem[] = [];
  dskSearchLoading = false;
  vendorSearchLoading = false;
  selectedDskSearch: DskGroupSearchItem | null = null;
  selectedVendorSearch: VendorSearchItem | null = null;
  formatDskSearchOptionLabel = formatDskSearchOptionLabel;
  formatDskSearchOptionMeta = formatDskSearchOptionMeta;
  private destroy$ = new Subject<void>();

  // Multi-buy offers
  multiBuyOffers: any[] = [];
  offersLoading = false;
  offerSaving = false;
  showOfferForm = false;
  editingOfferId: string | null = null;
  offerRequiredQuantity: number | null = null;
  offerPrice: number | null = null;
  offerValidFrom = '';
  offerValidTo = '';
  offerQtyTouched = false;
  offerPriceTouched = false;
  offerInlineError = '';

  discountGroupOffersForProduct: any[] = [];
  discountGroupOffersLoading = false;

  constructor(
    private dialogRef: NbDialogRef<ProductFormDialogComponent>,
    private dialogService: NbDialogService,
    private ecommerceService: EcommerceService,
    private imageUrlService: ImageUrlService,
    private globalProductService: GlobalProductService,
    private globalCategoryService: GlobalCategoryService,
    private globalReferenceService: GlobalReferenceService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private elementRef: ElementRef,
    private productFeatureFlagsService: ProductFeatureFlagsService
  ) {}

  static unitContentValidator(control: AbstractControl): ValidationErrors | null {
    const unitCode = control.parent?.get('unitCode')?.value;
    const unitContent = control.value;
    if (isLiquidUnitCode(unitCode) && unitContent === 0) {
      return {
        unitContentZero: {
          message: 'Unit content must be greater than 0 for liquid unit codes (CL, ML, L)'
        }
      };
    }
    return null;
  }

  ngOnInit(): void {
    this.initializeProductForm();
    this.productFeatureFlagsService.getFlags().pipe(takeUntil(this.destroy$)).subscribe((flags) => {
      this.showAllergens = flags.productAllergensEnabled;
      this.showNutrition = flags.productNutritionEnabled;
      this.applyProductFeatureFlagControls();
    });
    if (this.product) {
      this.patchFormWithProduct(this.product);
      this.loadCurrentPlacement();
      this.loadPricingData();
    } else {
      this.productForm.patchValue({
        taxRate: 25,
        taxIncluded: true,
        isVisible: true,
        isFeatured: false,
        trackInventory: true,
        lowStockThreshold: 10,
        minimumAge: 0,
        ageRestricted: false,
        hasDeposit: false,
        hasConsumptionLimit: false
      });
    }
    this.updateSkuValidation();
    // Apply soldByWeight validator state on form open (handles edit case where soldByWeight is already true)
    const isByWeight = this.productForm.get('soldByWeight')!.value;
    const priceControl = this.productForm.get('price')!;
    const pricePerKgControl = this.productForm.get('pricePerKg')!;
    if (isByWeight) {
      priceControl.clearValidators();
      pricePerKgControl.setValidators([Validators.required, Validators.min(0.01)]);
    } else {
      priceControl.setValidators([Validators.required, Validators.min(0.01)]);
      pricePerKgControl.setValidators([Validators.min(0)]);
    }
    priceControl.updateValueAndValidity();
    pricePerKgControl.updateValueAndValidity();

    this.productForm
      .get('discountGroup')
      ?.valueChanges.pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(() => this.loadDiscountGroupOffersForProduct());

    this.productForm
      .get('categoryId')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.catalogImportCategoryHint = '';
      });

    if (!this.product) {
      this.globalPanelOpen = true;
    }

    // Initialize global product link state from product data
    if (this.product?.globalProductId) {
      this.fieldLocks = { ...(this.product.fieldLocks || {}) };
      this.globalPanelOpen = true;
      // Load the linked global product info
      this.globalProductService.getGlobalProduct(this.product.globalProductId).subscribe({
        next: (res: any) => {
          this.selectedGlobalProduct = res?.response?.data || res?.data || null;
        },
        error: () => { /* ignore */ }
      });
    }

    this.setupReferenceTypeaheads();

    // Debounced search
    this.globalSearchSubject.pipe(
      debounceTime(350),
      distinctUntilChanged()
    ).subscribe((term) => {
      if (term.length < 2) {
        this.globalSearchResults = [];
        return;
      }
      this.globalSearchLoading = true;
      const filters: { search: string; excludeStoreId?: string } = { search: term };
      if (!this.product && this.storeId) {
        filters.excludeStoreId = this.storeId;
      }
      this.globalProductService.listPlatformProducts(filters).subscribe({
        next: (res: any) => {
          this.globalSearchResults = res?.response?.data?.products || res?.data?.products || [];
          this.globalSearchLoading = false;
        },
        error: () => {
          this.globalSearchLoading = false;
        }
      });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupReferenceTypeaheads(): void {
    this.dskSearchInput$.pipe(
      debounceTime(250),
      distinctUntilChanged(),
      switchMap((term) => {
        if (!term || term.length < 1) {
          return of({ items: [] as DskGroupSearchItem[] });
        }
        this.dskSearchLoading = true;
        return this.globalReferenceService.searchDskGroups({ q: term, limit: 50 }).pipe(
          catchError(() => of({ items: [] as DskGroupSearchItem[], total: 0 })),
          finalize(() => { this.dskSearchLoading = false; })
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe((res) => {
      this.dskSearchResults = res.items || [];
    });

    this.vendorSearchInput$.pipe(
      debounceTime(250),
      distinctUntilChanged(),
      switchMap((term) => {
        if (!term || term.length < 1) {
          return of({ items: [] as VendorSearchItem[] });
        }
        this.vendorSearchLoading = true;
        return this.globalReferenceService.searchVendors(term, 50).pipe(
          catchError(() => of({ items: [] as VendorSearchItem[], total: 0 })),
          finalize(() => { this.vendorSearchLoading = false; })
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe((res) => {
      this.vendorSearchResults = res.items || [];
    });
  }

  onDskSearchSelected(item: DskGroupSearchItem | null): void {
    if (!item) {
      return;
    }
    patchDskClassification(this.productForm, item);
    this.selectedDskSearch = null;
  }

  onVendorSearchSelected(item: VendorSearchItem | null): void {
    if (!item) {
      return;
    }
    patchVendorFields(this.productForm, item);
    this.selectedVendorSearch = null;
  }

  get showGlobalImagePreview(): boolean {
    return !this.product
      && !!this.pendingGlobalProductLink
      && this.productImages.length === 0
      && this.productImagePreviews.length === 0
      && this.getPendingGlobalImageUrls().length > 0;
  }

  getPendingGlobalImageUrls(): string[] {
    const gp = this.pendingGlobalProductLink || this.selectedGlobalProduct;
    if (!gp?.images?.length) {
      return [];
    }
    return (gp.images as any[])
      .map((img: any) => this.imageUrlService.fixImageUrl(typeof img === 'string' ? img : img?.url || ''))
      .filter((url: string) => !!url && url !== 'null' && !url.endsWith('/null'));
  }

  private initializeProductForm(): void {
    this.productForm = new FormGroup({
      name: new FormControl('', [Validators.required, Validators.minLength(2)]),
      description: new FormControl(''),
      sku: new FormControl(''),
      barcode: new FormControl('', [barcodeValidator]),
      price: new FormControl(0, [Validators.required, Validators.min(0.01)]),
      originalPrice: new FormControl(0, [Validators.min(0)]),
      taxRate: new FormControl(25, [Validators.min(0), Validators.max(100)]),
      taxIncluded: new FormControl(true),
      categoryId: new FormControl('', [Validators.required]),
      isVisible: new FormControl(true),
      isFeatured: new FormControl(false),
      quantity: new FormControl(0, [Validators.required, Validators.min(0)]),
      trackInventory: new FormControl(true),
      lowStockThreshold: new FormControl(10, [Validators.min(0)]),
      ageRestricted: new FormControl(false),
      minimumAge: new FormControl(0, [Validators.min(0), Validators.max(100)]),
      hasDeposit: new FormControl(false),
      depositQuantity: new FormControl(1, [Validators.required, Validators.min(1)]),
      depositCategory: new FormControl(''),
      depositFeeTypeId: new FormControl(''),
      hasConsumptionLimit: new FormControl(false),
      limitType: new FormControl(''),
      maxQuantity: new FormControl(null),
      excludeFromLoyaltyDiscount: new FormControl(false),
      costPrice: new FormControl(null, [Validators.min(0)]),
      providerDepositCode: new FormControl(''),
      plu: new FormControl('', [Validators.maxLength(7)]),
      soldByWeight: new FormControl(false),
      pricePerKg: new FormControl(null, [Validators.min(0)]),
      weightEncoding: new FormControl('price'),
      mainGroup: new FormControl(''),
      productGroup: new FormControl(''),
      subGroup: new FormControl(''),
      dskAssortmentID: new FormControl(''),
      family: new FormControl(''),
      discountGroup: new FormControl(''),
      unitCode: new FormControl(''),
      unitContent: new FormControl(null, [ProductFormDialogComponent.unitContentValidator]),
      ingredients: new FormControl(''),
      allergens: new FormControl<string[]>([]),
      allergenTraces: new FormControl<string[]>([]),
      nutrition: createNutritionFormGroup(),
      vendorID: new FormControl(''),
      vendorName: new FormControl(''),
      orderNumber: new FormControl(''),
      producer: new FormControl(''),
      country: new FormControl(''),
      coli: new FormControl(null, [coliValidator]),
      shelfLifeDays: new FormControl(null, [shelfLifeDaysValidator]),
      ...createPlacementFormControls()
    });

    this.productForm.get('unitCode')?.valueChanges.subscribe(() => {
      this.useLiveLiterPricePreview = true;
      this.productForm.get('unitContent')?.updateValueAndValidity({ emitEvent: false });
    });

    this.productForm.get('unitContent')?.valueChanges.subscribe(() => {
      this.useLiveLiterPricePreview = true;
    });

    this.productForm.get('price')?.valueChanges.subscribe(() => {
      this.useLiveLiterPricePreview = true;
    });

    this.productForm.get('ageRestricted')?.valueChanges.subscribe(ageRestricted => {
      const minimumAgeControl = this.productForm.get('minimumAge');
      if (ageRestricted) {
        minimumAgeControl?.setValidators([Validators.required, Validators.min(1), Validators.max(100)]);
      } else {
        minimumAgeControl?.setValidators([Validators.min(0), Validators.max(100)]);
        minimumAgeControl?.setValue(0);
      }
      minimumAgeControl?.updateValueAndValidity();
    });

    this.productForm.get('hasDeposit')?.valueChanges.subscribe(hasDeposit => {
      const depositCategoryControl = this.productForm.get('depositCategory');
      const depositFeeTypeIdControl = this.productForm.get('depositFeeTypeId');
      if (hasDeposit) {
        // Only require feeTypeId when catalog has entries; fall back to legacy category
        if (this.depositFeeTypes.length > 0) {
          depositFeeTypeIdControl?.setValidators([Validators.required]);
          depositCategoryControl?.setValidators([]);
        } else {
          depositCategoryControl?.setValidators([Validators.required]);
          depositFeeTypeIdControl?.setValidators([]);
        }
      } else {
        depositCategoryControl?.setValidators([]);
        depositCategoryControl?.setValue('');
        depositFeeTypeIdControl?.setValidators([]);
        depositFeeTypeIdControl?.setValue('');
        this.productForm.get('depositQuantity')?.setValue(1);
        this.eanAutoDetectHint = '';
      }
      depositCategoryControl?.updateValueAndValidity();
      depositFeeTypeIdControl?.updateValueAndValidity();
    });

    this.productForm.get('hasConsumptionLimit')?.valueChanges.subscribe(hasLimit => {
      const limitTypeControl = this.productForm.get('limitType');
      const maxQuantityControl = this.productForm.get('maxQuantity');
      if (hasLimit) {
        limitTypeControl?.setValidators([Validators.required]);
        maxQuantityControl?.setValidators([Validators.required, Validators.min(1)]);
      } else {
        limitTypeControl?.setValidators([]);
        maxQuantityControl?.setValidators([]);
        limitTypeControl?.setValue('');
        maxQuantityControl?.setValue(null);
      }
      limitTypeControl?.updateValueAndValidity();
      maxQuantityControl?.updateValueAndValidity();
    });

    this.productForm.get('soldByWeight')!.valueChanges.subscribe((isByWeight: boolean) => {
      const priceControl = this.productForm.get('price')!;
      const pricePerKgControl = this.productForm.get('pricePerKg')!;
      if (isByWeight) {
        priceControl.clearValidators();
        priceControl.setValue(0);
        pricePerKgControl.setValidators([Validators.required, Validators.min(0.01)]);
      } else {
        priceControl.setValidators([Validators.required, Validators.min(0.01)]);
        pricePerKgControl.setValidators([Validators.min(0)]);
      }
      priceControl.updateValueAndValidity();
      pricePerKgControl.updateValueAndValidity();
    });
  }

  private patchFormWithProduct(product: any): void {
    this.productForm.patchValue({
      name: product.name,
      description: product.description || '',
      sku: product.sku,
      barcode: product.barcode || '',
      price: product.price,
      originalPrice: product.originalPrice || 0,
      taxRate: product.taxRate || 25,
      taxIncluded: product.taxIncluded !== false,
      categoryId: product.categoryId?._id || product.categoryId,
      isVisible: product.isVisible !== false,
      isFeatured: product.isFeatured === true,
      quantity: product.inventory?.quantity || 0,
      trackInventory: product.inventory?.trackInventory !== false,
      lowStockThreshold: product.inventory?.lowStockThreshold || 10,
      ageRestricted: product.ageRestricted === true,
      minimumAge: product.minimumAge || 0,
      hasDeposit: product.hasDeposit === true,
      depositQuantity: product.depositQuantity || 1,
      depositCategory: product.depositCategory || '',
      depositFeeTypeId: (typeof product.depositFeeTypeId === 'object' ? product.depositFeeTypeId?._id : product.depositFeeTypeId) || '',
      hasConsumptionLimit: product.hasConsumptionLimit === true,
      limitType: product.consumptionLimit?.limitType || '',
      maxQuantity: product.consumptionLimit?.maxQuantity || null,
      excludeFromLoyaltyDiscount: product.excludeFromLoyaltyDiscount === true,
      costPrice: product.costPrice ?? null,
      providerDepositCode: product.providerDepositCode ?? '',
      plu: product.plu ?? '',
      soldByWeight: product.soldByWeight === true,
      pricePerKg: product.pricePerKg ?? null,
      weightEncoding: product.weightEncoding ?? 'price',
      mainGroup: product.mainGroup || '',
      productGroup: product.productGroup || '',
      subGroup: product.subGroup || '',
      dskAssortmentID: product.dskAssortmentID || '',
      family: product.family || '',
      discountGroup: product.discountGroup || '',
      unitCode: product.unitCode || '',
      unitContent: product.unitContent ?? null
    });
    this.serverLiterPrice = product.literPrice ?? null;
    this.useLiveLiterPricePreview = false;
    patchIngredientsNutrition(this.productForm, product);
    patchVendorOrder(this.productForm, product);
    this.applyProductFeatureFlagControls();
    patchPlacement(this.productForm, product.currentPlacement);
    this.savedPlacementSnapshot = snapshotPlacementValues(this.productForm.value);
    this.productImages = [];
    this.productImagePreviews = this.imageUrlService.getProductImageUrls(product);
    this.primaryImageIndex = product.images?.findIndex((img: any) => img.isPrimary) || 0;
  }

  private updateSkuValidation(): void {
    const skuControl = this.productForm.get('sku');
    if (skuControl) {
      if (this.skuRequirementEnabled) {
        skuControl.setValidators([Validators.required]);
      } else {
        skuControl.setValidators([]);
      }
      skuControl.updateValueAndValidity();
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  onProductImagesSelect(event: any): void {
    const files = Array.from(event.target.files) as File[];
    files.forEach(file => {
      if (file.type.startsWith('image/')) {
        this.productImages.push(file);
        const reader = new FileReader();
        reader.onload = (e: any) => {
          this.productImagePreviews.push(e.target.result);
        };
        reader.readAsDataURL(file);
      }
    });
  }

  removeProductImage(index: number): void {
    this.productImages.splice(index, 1);
    this.productImagePreviews.splice(index, 1);
    if (this.primaryImageIndex >= index && this.primaryImageIndex > 0) {
      this.primaryImageIndex--;
    }
  }

  setPrimaryImage(index: number): void {
    this.primaryImageIndex = index;
  }

  onSubmitProduct(): void {
    if (this.productForm.invalid) {
      this.markFormGroupTouched(this.productForm);
      const invalidLabels = this.collectInvalidFieldLabels();
      const barcodeMessage = this.productForm.get('barcode')?.errors?.['invalidBarcode']?.message;
      if (invalidLabels.length) {
        this.toastr.error(`Missing or invalid: ${invalidLabels.join(', ')}`);
      } else {
        this.toastr.error(barcodeMessage || 'Please fill in all required fields');
      }
      this.scrollToFirstInvalidControl();
      return;
    }
    if (this.product) {
      this.updateProduct();
    } else {
      this.createProduct();
    }
  }

  private extractCreatedProductId(res: any): string | null {
    const data = res?.response?.data ?? res?.data;
    if (!data || typeof data !== 'object') {
      return null;
    }
    return data._id || data.product?._id || data.id || null;
  }

  private createProduct(): void {
    this.spinner.show();
    const formData = new FormData();
    const formValue = this.productForm.value;
    const pendingLink = this.pendingGlobalProductLink;
    const globalProductId = pendingLink?._id || pendingLink?.id || null;

    formData.append('name', formValue.name);
    formData.append('description', formValue.description || '');
    if ((this.skuRequirementEnabled || pendingLink) && formValue.sku) {
      formData.append('sku', formValue.sku);
    }
    if (formValue.barcode) {
      formData.append('barcode', formValue.barcode);
    }
    formData.append('price', formValue.price.toString());
    formData.append('originalPrice', formValue.originalPrice?.toString() || '0');
    formData.append('taxRate', formValue.taxRate.toString());
    formData.append('taxIncluded', formValue.taxIncluded.toString());
    formData.append('categoryId', formValue.categoryId);
    formData.append('isVisible', formValue.isVisible.toString());
    formData.append('isFeatured', formValue.isFeatured.toString());
    formData.append('inventory[quantity]', formValue.quantity.toString());
    formData.append('inventory[trackInventory]', formValue.trackInventory.toString());
    formData.append('inventory[lowStockThreshold]', formValue.lowStockThreshold.toString());
    formData.append('ageRestricted', formValue.ageRestricted.toString());
    formData.append('minimumAge', (formValue.minimumAge || 0).toString());
    formData.append('hasDeposit', formValue.hasDeposit.toString());
    if (formValue.hasDeposit) {
      formData.append('depositQuantity', (formValue.depositQuantity || 1).toString());
      if (formValue.depositFeeTypeId) {
        formData.append('depositFeeTypeId', formValue.depositFeeTypeId);
      }
      if (formValue.depositCategory) {
        formData.append('depositCategory', formValue.depositCategory);
      }
      if (formValue.providerDepositCode) {
        formData.append('providerDepositCode', formValue.providerDepositCode);
      }
    }
    formData.append('hasConsumptionLimit', formValue.hasConsumptionLimit.toString());
    if (formValue.hasConsumptionLimit) {
      formData.append('consumptionLimit[limitType]', formValue.limitType);
      formData.append('consumptionLimit[maxQuantity]', formValue.maxQuantity.toString());
    } else {
      formData.append('consumptionLimit[limitType]', '');
      formData.append('consumptionLimit[maxQuantity]', '');
    }
    formData.append('excludeFromLoyaltyDiscount', (formValue.excludeFromLoyaltyDiscount || false).toString());
    const costPriceVal = this.productForm.get('costPrice')?.value;
    if (costPriceVal !== null && costPriceVal !== '' && costPriceVal !== undefined) {
      formData.append('costPrice', costPriceVal);
    }
    if (formValue.plu) {
      formData.append('plu', formValue.plu);
    }
    formData.append('mainGroup', formValue.mainGroup || '');
    formData.append('productGroup', formValue.productGroup || '');
    formData.append('subGroup', formValue.subGroup || '');
    formData.append('dskAssortmentID', formValue.dskAssortmentID || '');
    formData.append('family', formValue.family || '');
    formData.append('discountGroup', formValue.discountGroup || '');
    formData.append('unitCode', formValue.unitCode || '');
    if (formValue.unitContent !== null && formValue.unitContent !== undefined && formValue.unitContent !== '') {
      formData.append('unitContent', formValue.unitContent.toString());
    } else {
      formData.append('unitContent', '');
    }
    appendIngredientsNutritionFormData(formData, formValue, {
      includeAllergens: this.showAllergens,
      includeNutrition: this.showNutrition,
    });
    appendVendorOrderFormData(formData, formValue);
    formData.append('soldByWeight', (formValue.soldByWeight || false).toString());
    if (formValue.soldByWeight) {
      if (formValue.pricePerKg !== null && formValue.pricePerKg !== undefined) {
        formData.append('pricePerKg', formValue.pricePerKg.toString());
      }
      formData.append('weightEncoding', formValue.weightEncoding || 'price');
    }
    this.productImages.forEach((file, index) => {
      formData.append('images', file);
      formData.append(`imageAlt_${index}`, file.name);
      formData.append(`isPrimary_${index}`, (index === this.primaryImageIndex).toString());
    });

    this.ecommerceService.createProduct(this.storeId, formData).pipe(
      switchMap((res: any) => {
        if (!isApiSuccess(res)) {
          return throwError(() => ({
            message: res?.response?.message || 'Product creation failed'
          }));
        }

        const newProductId = this.extractCreatedProductId(res);
        if (!newProductId) {
          return throwError(() => ({
            message: 'Product created but product ID was missing from the response'
          }));
        }

        if (!pendingLink || !globalProductId) {
          return of({ created: true, linked: false, productId: newProductId });
        }

        return this.globalProductService.linkProductToGlobal(
          this.storeId, newProductId, globalProductId
        ).pipe(
          switchMap((linkRes: any) => {
            if (isApiSuccess(linkRes)) {
              return of({ created: true, linked: true, productId: newProductId });
            }
            return throwError(() => ({
              productCreated: true,
              productId: newProductId,
              linked: false,
              message: linkRes?.response?.message || 'Failed to link global product'
            }));
          }),
          catchError((err) => throwError(() => ({
            productCreated: true,
            productId: newProductId,
            linked: false,
            message: extractApiErrorMessage(err, 'Failed to link global product')
          })))
        );
      }),
      switchMap((result: any) => {
        if (!result?.productId || !this.shouldSavePlacementOnCreate()) {
          return of(result);
        }
        return this.saveProductPlacement(result.productId).pipe(
          map(() => ({ ...result, placementSaved: true })),
          catchError((err) => throwError(() => ({
            productCreated: true,
            productId: result.productId,
            linked: result.linked,
            message: extractApiErrorMessage(err, 'Failed to save product placement')
          })))
        );
      }),
      catchError((err) => {
        if (err?.productCreated) {
          return throwError(() => err);
        }
        return throwError(() => ({
          message: extractApiErrorMessage(err, 'Failed to create product')
        }));
      }),
      finalize(() => this.spinner.hide())
    ).subscribe({
      next: (result) => {
        this.toastr.success('Product created successfully');
        if (result.linked) {
          this.toastr.success('Linked to global product');
        }
        if (result.placementSaved) {
          this.toastr.success('Store placement saved');
        }
        this.dialogRef.close({ saved: true, linked: result.linked });
      },
      error: (err: any) => {
        if (err?.productCreated) {
          this.toastr.success('Product created successfully');
          if (err.linked) {
            this.toastr.success('Linked to global product');
          }
          this.toastr.error(err.message || 'Failed to save product placement');
          this.dialogRef.close({ saved: true, linked: !!err.linked });
          return;
        }
        console.error('Failed to create product:', err);
        this.toastr.error(err?.message || 'Failed to create product');
      }
    });
  }

  private updateProduct(): void {
    this.spinner.show();
    const formData = new FormData();
    const formValue = this.productForm.value;

    formData.append('name', formValue.name);
    formData.append('description', formValue.description || '');
    if (this.skuRequirementEnabled && formValue.sku) {
      formData.append('sku', formValue.sku);
    }
    if (formValue.barcode) {
      formData.append('barcode', formValue.barcode);
    }
    formData.append('price', formValue.price.toString());
    formData.append('originalPrice', formValue.originalPrice?.toString() || '0');
    formData.append('taxRate', formValue.taxRate.toString());
    formData.append('taxIncluded', formValue.taxIncluded.toString());
    formData.append('categoryId', formValue.categoryId);
    formData.append('isVisible', formValue.isVisible.toString());
    formData.append('isFeatured', formValue.isFeatured.toString());
    formData.append('inventory[quantity]', formValue.quantity.toString());
    formData.append('inventory[trackInventory]', formValue.trackInventory.toString());
    formData.append('inventory[lowStockThreshold]', formValue.lowStockThreshold.toString());
    formData.append('ageRestricted', formValue.ageRestricted.toString());
    formData.append('minimumAge', (formValue.minimumAge || 0).toString());
    formData.append('hasDeposit', formValue.hasDeposit.toString());
    if (formValue.hasDeposit) {
      formData.append('depositQuantity', (formValue.depositQuantity || 1).toString());
      if (formValue.depositFeeTypeId) {
        formData.append('depositFeeTypeId', formValue.depositFeeTypeId);
      }
      if (formValue.depositCategory) {
        formData.append('depositCategory', formValue.depositCategory);
      }
      if (formValue.providerDepositCode) {
        formData.append('providerDepositCode', formValue.providerDepositCode);
      }
    }
    formData.append('hasConsumptionLimit', formValue.hasConsumptionLimit.toString());
    if (formValue.hasConsumptionLimit) {
      formData.append('consumptionLimit[limitType]', formValue.limitType);
      formData.append('consumptionLimit[maxQuantity]', formValue.maxQuantity.toString());
    } else {
      formData.append('consumptionLimit[limitType]', '');
      formData.append('consumptionLimit[maxQuantity]', '');
    }
    formData.append('excludeFromLoyaltyDiscount', (formValue.excludeFromLoyaltyDiscount || false).toString());
    const costPriceVal = this.productForm.get('costPrice')?.value;
    if (costPriceVal !== null && costPriceVal !== '' && costPriceVal !== undefined) {
      formData.append('costPrice', costPriceVal);
    }
    if (formValue.plu) {
      formData.append('plu', formValue.plu);
    }
    formData.append('mainGroup', formValue.mainGroup || '');
    formData.append('productGroup', formValue.productGroup || '');
    formData.append('subGroup', formValue.subGroup || '');
    formData.append('dskAssortmentID', formValue.dskAssortmentID || '');
    formData.append('family', formValue.family || '');
    formData.append('discountGroup', formValue.discountGroup || '');
    formData.append('unitCode', formValue.unitCode || '');
    if (formValue.unitContent !== null && formValue.unitContent !== undefined && formValue.unitContent !== '') {
      formData.append('unitContent', formValue.unitContent.toString());
    } else {
      formData.append('unitContent', '');
    }
    appendIngredientsNutritionFormData(formData, formValue, {
      includeAllergens: this.showAllergens,
      includeNutrition: this.showNutrition,
    });
    appendVendorOrderFormData(formData, formValue);
    formData.append('soldByWeight', (formValue.soldByWeight || false).toString());
    if (formValue.soldByWeight) {
      if (formValue.pricePerKg !== null && formValue.pricePerKg !== undefined) {
        formData.append('pricePerKg', formValue.pricePerKg.toString());
      }
      formData.append('weightEncoding', formValue.weightEncoding || 'price');
    }
    if (this.productImages.length > 0) {
      this.productImages.forEach((file, index) => {
        formData.append('images', file);
        formData.append(`imageAlt_${index}`, file.name);
        formData.append(`isPrimary_${index}`, (index === this.primaryImageIndex).toString());
      });
    }

    const placementChanged = this.hasPlacementChanged();

    this.ecommerceService.updateProduct(this.storeId, this.product._id, formData).pipe(
      switchMap((res: any) => {
        if (!isApiSuccess(res)) {
          return throwError(() => ({
            message: res?.response?.message || 'Failed to update product'
          }));
        }
        if (!placementChanged) {
          return of({ res, placementSaved: false });
        }
        return this.saveProductPlacement(this.product._id).pipe(
          map(() => ({ res, placementSaved: true })),
          catchError((err) => throwError(() => ({
            productUpdated: true,
            message: extractApiErrorMessage(err, 'Failed to save product placement')
          })))
        );
      }),
      finalize(() => this.spinner.hide())
    ).subscribe({
      next: (result: any) => {
        this.toastr.success('Product updated successfully');
        if (result.placementSaved) {
          this.toastr.success('Store placement updated');
        }
        this.dialogRef.close({ saved: true });
      },
      error: (error: any) => {
        if (error?.productUpdated) {
          this.toastr.success('Product updated successfully');
          this.toastr.error(error.message || 'Failed to save product placement');
          this.dialogRef.close({ saved: true });
          return;
        }
        console.error('Failed to update product:', error);
        this.toastr.error(error?.message || extractApiErrorMessage(error, 'Failed to update product'));
      }
    });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      } else {
        control?.markAsTouched();
      }
    });
  }

  private collectInvalidFieldLabels(): string[] {
    const labels: string[] = [];
    const priority = ['categoryId', 'price', 'name', 'barcode', 'sku', 'quantity'];
    const addIfInvalid = (name: string) => {
      const control = this.productForm.get(name);
      if (control?.invalid) {
        labels.push(this.invalidControlLabels[name] || name);
      }
    };
    priority.forEach(addIfInvalid);
    Object.keys(this.invalidControlLabels).forEach((name) => {
      if (!priority.includes(name)) {
        addIfInvalid(name);
      }
    });
    return [...new Set(labels)];
  }

  private scrollToFirstInvalidControl(): void {
    const host = this.elementRef.nativeElement as HTMLElement;
    const invalid = host.querySelector('.ng-invalid[formcontrolname]') as HTMLElement | null;
    if (invalid) {
      invalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
      invalid.focus();
    }
  }

  formatCurrency(amount: number): string {
    if (!amount) return '0.00';
    const currencyLocaleMap: { [key: string]: string } = {
      'DKK': 'da-DK',
      'EUR': 'de-DE',
      'USD': 'en-US',
      'GBP': 'en-GB',
      'SEK': 'sv-SE',
      'NOK': 'nb-NO'
    };
    const locale = currencyLocaleMap[this.storeCurrency] || 'da-DK';
    const currency = this.storeCurrency || 'DKK';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency
    }).format(amount);
  }

  getDepositAmount(category: string): number {
    if (!category || !this.depositSettings.enabled) return 0;
    switch (category) {
      case 'A': return this.depositSettings.categoryA;
      case 'B': return this.depositSettings.categoryB;
      case 'C': return this.depositSettings.categoryC;
      default: return 0;
    }
  }

  getDepositAmountByFeeTypeId(feeTypeId: string): number {
    if (!feeTypeId) return 0;
    const ft = this.depositFeeTypes.find((f: any) => f._id === feeTypeId);
    return ft?.defaultAmount ?? 0;
  }

  onBarcodeInput(): void {
    const barcodeControl = this.productForm.get('barcode');
    barcodeControl?.markAsTouched();
    barcodeControl?.updateValueAndValidity({ emitEvent: false });
  }

  onBarcodeBlur(): void {
    this.onBarcodeInput();
    if (!this.autoAssignByEAN) return;
    const ean = this.productForm.get('barcode')?.value?.toString().trim();
    if (!ean || ean.length !== 13) return;

    this.detectingEAN = true;
    this.eanAutoDetectHint = '';
    this.ecommerceService.detectDepositFeeTypeByEAN(ean).subscribe(
      (res: any) => {
        this.detectingEAN = false;
        const feeType = res?.response?.data?.feeType ?? res?.data?.feeType ?? res?.feeType ?? null;
        if (feeType && feeType._id) {
          this.productForm.patchValue({
            hasDeposit: true,
            depositFeeTypeId: feeType._id
          });
          this.eanAutoDetectHint = `Deposit type auto-detected from EAN range: ${feeType.name}`;
        }
      },
      () => {
        this.detectingEAN = false;
      }
    );
  }

  getConsumptionLimitPreview(): string {
    const hasLimit = this.productForm.get('hasConsumptionLimit')?.value;
    const limitType = this.productForm.get('limitType')?.value;
    const maxQuantity = this.productForm.get('maxQuantity')?.value;
    if (hasLimit && limitType && maxQuantity) {
      return `Customer can purchase up to ${maxQuantity} of this product per ${limitType}.`;
    }
    return 'Customer can purchase up to [quantity] of this product per [period].';
  }

  // ===================== Global Product Linking =====================

  onGlobalSearchInput(term: string): void {
    this.globalSearchSubject.next(term);
  }

  selectGlobalProductResult(gp: any): void {
    this.globalSearchResults = [];
    this.globalSearchTerm = '';

    if (this.product?._id) {
      // Existing product — link immediately
      this.globalLinkLoading = true;
      this.globalProductService.linkProductToGlobal(this.storeId, this.product._id, gp._id).subscribe({
        next: () => {
          this.globalLinkLoading = false;
          this.selectedGlobalProduct = gp;
          if (this.product) this.product.globalProductId = gp._id;
          this.toastr.success('Linked to global product');
        },
        error: (err: any) => {
          this.globalLinkLoading = false;
          this.toastr.error(err?.error?.response?.message || 'Link failed');
        }
      });
    } else {
      // New product — set pending link and pre-fill form
      this.pendingGlobalProductLink = gp;
      this.selectedGlobalProduct = gp;
      this.preFillFromGlobalProduct(gp);
    }
  }

  preFillFromGlobalProduct(gp: any): void {
    if (gp.name) this.productForm.get('name')?.setValue(gp.name);
    if (gp.description) this.productForm.get('description')?.setValue(gp.description);
    if (gp.barcode) this.productForm.get('barcode')?.setValue(gp.barcode);
    if (gp.sku) this.productForm.get('sku')?.setValue(gp.sku);
    if (gp.ageRestricted != null) this.productForm.get('ageRestricted')?.setValue(gp.ageRestricted);
    if (gp.minimumAge != null) this.productForm.get('minimumAge')?.setValue(gp.minimumAge);
    if (gp.hasDeposit != null) this.productForm.get('hasDeposit')?.setValue(gp.hasDeposit);
    if (gp.depositCategory) this.productForm.get('depositCategory')?.setValue(gp.depositCategory);
    const feeTypeId = typeof gp.depositFeeTypeId === 'object' ? gp.depositFeeTypeId?._id : gp.depositFeeTypeId;
    if (feeTypeId) this.productForm.get('depositFeeTypeId')?.setValue(feeTypeId);
    if (gp.providerDepositCode) this.productForm.get('providerDepositCode')?.setValue(gp.providerDepositCode);
    if (gp.mainGroup) this.productForm.get('mainGroup')?.setValue(gp.mainGroup);
    if (gp.productGroup) this.productForm.get('productGroup')?.setValue(gp.productGroup);
    if (gp.subGroup) this.productForm.get('subGroup')?.setValue(gp.subGroup);
    if (gp.dskAssortmentID) this.productForm.get('dskAssortmentID')?.setValue(gp.dskAssortmentID);
    if (gp.family) this.productForm.get('family')?.setValue(gp.family);
    if (gp.discountGroup) this.productForm.get('discountGroup')?.setValue(gp.discountGroup);
    if (gp.unitCode) this.productForm.get('unitCode')?.setValue(gp.unitCode);
    if (gp.unitContent != null) this.productForm.get('unitContent')?.setValue(gp.unitContent);
    if (gp.defaultSellPrice != null && gp.defaultSellPrice !== '') {
      this.productForm.get('price')?.setValue(gp.defaultSellPrice);
    }
    if (gp.defaultCostPrice != null && gp.defaultCostPrice !== '') {
      this.productForm.get('costPrice')?.setValue(gp.defaultCostPrice);
    }
    if (!this.product) {
      this.resolveCategoryForGlobalImport(gp);
    }
    patchIngredientsNutrition(this.productForm, gp);
    patchVendorOrder(this.productForm, gp);
    this.applyProductFeatureFlagControls();
    this.useLiveLiterPricePreview = true;
  }

  private resolveCategoryForGlobalImport(gp: any): void {
    const globalCategoryId = gp.globalCategoryId || gp.globalCategory?._id || null;
    this.globalCategoryService.resolveStoreCategory(this.storeId, globalCategoryId || undefined).subscribe({
      next: (res: any) => {
        const resolved = this.globalCategoryService.extractResolve(res);
        if (this.applyResolvedStoreCategory(resolved.storeCategoryId, resolved.source)) {
          return;
        }
        this.applyCatalogImportDefaultCategoryFallback();
      },
      error: () => {
        this.applyCatalogImportDefaultCategoryFallback();
      }
    });
  }

  private applyResolvedStoreCategory(storeCategoryId: string | null, source: string): boolean {
    const categoryControl = this.productForm.get('categoryId');
    const currentCategoryId = categoryControl?.value;
    if (currentCategoryId) {
      return true;
    }
    if (!storeCategoryId || !this.categories.some((c: any) => c._id === storeCategoryId)) {
      return false;
    }
    categoryControl?.setValue(storeCategoryId);
    this.catalogImportCategoryHint = this.hintForCategorySource(source);
    return true;
  }

  private hintForCategorySource(source: string): string {
    switch (source) {
      case 'mapping':
        return 'From global category mapping';
      case 'name':
        return 'Matched by name';
      case 'default':
        return 'Store default for catalog imports';
      default:
        return '';
    }
  }

  private applyCatalogImportDefaultCategoryFallback(): void {
    if (!this.catalogImportDefaultCategoryId) {
      return;
    }
    const categoryControl = this.productForm.get('categoryId');
    const currentCategoryId = categoryControl?.value;
    if (!currentCategoryId && this.categories.some((c: any) => c._id === this.catalogImportDefaultCategoryId)) {
      categoryControl?.setValue(this.catalogImportDefaultCategoryId);
      this.catalogImportCategoryHint = 'Store default for catalog imports';
    }
  }

  clearPendingLink(): void {
    this.pendingGlobalProductLink = null;
    this.selectedGlobalProduct = null;
    this.catalogImportCategoryHint = '';
  }

  unlinkGlobalProduct(): void {
    if (!this.product?._id) return;
    this.globalLinkLoading = true;
    this.globalProductService.unlinkProduct(this.storeId, this.product._id).subscribe({
      next: () => {
        this.globalLinkLoading = false;
        this.selectedGlobalProduct = null;
        this.globalSearchTerm = '';
        this.fieldLocks = {};
        this.toastr.success('Global product link removed');
      },
      error: (err: any) => {
        this.globalLinkLoading = false;
        this.toastr.error(err?.error?.response?.message || 'Unlink failed');
      }
    });
  }

  toggleFieldLock(key: string): void {
    this.fieldLocks[key] = !this.fieldLocks[key];
  }

  saveFieldLocks(): void {
    if (!this.product?._id) return;
    this.fieldLocksSaving = true;
    this.globalProductService.updateFieldLocks(this.storeId, this.product._id, this.fieldLocks).subscribe({
      next: () => {
        this.fieldLocksSaving = false;
        this.toastr.success('Field locks saved');
      },
      error: (err: any) => {
        this.fieldLocksSaving = false;
        this.toastr.error(err?.error?.response?.message || 'Save failed');
      }
    });
  }

  get lockedFieldsCount(): number {
    return Object.values(this.fieldLocks).filter((v) => v === true).length;
  }

  get syncStatus(): 'synced' | 'overrides' {
    return this.lockedFieldsCount > 0 ? 'overrides' : 'synced';
  }

  get displayedLiterPrice(): string {
    const value = this.getDisplayedLiterPriceValue();
    if (value == null) {
      return 'N/A';
    }
    return formatLiterPriceDisplay(value);
  }

  private getDisplayedLiterPriceValue(): number | null {
    if (this.useLiveLiterPricePreview || !this.product) {
      return computeLiterPrice(
        this.productForm.get('price')?.value,
        this.productForm.get('unitCode')?.value,
        this.productForm.get('unitContent')?.value
      );
    }

    if (this.serverLiterPrice != null && Number.isFinite(Number(this.serverLiterPrice))) {
      return Number(this.serverLiterPrice);
    }

    return computeLiterPrice(
      this.productForm.get('price')?.value,
      this.productForm.get('unitCode')?.value,
      this.productForm.get('unitContent')?.value
    );
  }

  private applyProductFeatureFlagControls(): void {
    if (!this.productForm) {
      return;
    }
    for (const name of ['ingredients', 'allergens', 'allergenTraces'] as const) {
      const control = this.productForm.get(name);
      if (this.showAllergens) {
        control?.enable({ emitEvent: false });
      } else {
        control?.disable({ emitEvent: false });
      }
    }
    const nutrition = this.productForm.get('nutrition');
    if (this.showNutrition) {
      nutrition?.enable({ emitEvent: false });
    } else {
      nutrition?.disable({ emitEvent: false });
    }
  }

  get ingredientsCharCount(): number {
    return (this.productForm.get('ingredients')?.value || '').length;
  }

  isAllergenSelected(field: 'allergens' | 'allergenTraces', value: string): boolean {
    return isAllergenSelected(this.productForm.get(field)?.value, value);
  }

  toggleAllergen(field: 'allergens' | 'allergenTraces', value: string, checked: boolean): void {
    const control = this.productForm.get(field);
    const updated = toggleAllergenSelection(control?.value || [], value, checked);
    control?.setValue(updated);
    control?.markAsTouched();
  }

  openPlacementHistoryDialog(): void {
    if (!this.product?._id) {
      return;
    }
    this.dialogService.open(PlacementHistoryDialogComponent, {
      context: {
        storeId: this.storeId,
        productId: this.product._id,
        productName: this.productForm.get('name')?.value || this.product.name
      },
      hasBackdrop: true,
      closeOnBackdropClick: true
    });
  }

  openPriceHistoryDialog(): void {
    if (!this.product?._id) {
      return;
    }
    this.dialogService.open(PriceHistoryDialogComponent, {
      context: {
        storeId: this.storeId,
        productId: this.product._id,
        productName: this.productForm.get('name')?.value || this.product.name,
        storeCurrency: this.storeCurrency
      },
      hasBackdrop: true,
      closeOnBackdropClick: true
    });
  }

  toggleSchedulePanel(): void {
    this.schedulePanelOpen = !this.schedulePanelOpen;
    if (this.schedulePanelOpen) {
      this.scheduleInlineError = '';
    }
  }

  get schedulePriceError(): string | null {
    if (!this.schedulePriceTouched) {
      return null;
    }
    return scheduledPriceValidator({ value: this.schedulePrice } as any)?.['scheduledPrice']?.message || null;
  }

  get scheduleDateError(): string | null {
    if (!this.scheduleDateTouched) {
      return null;
    }
    return futureDateTimeValidator({ value: this.scheduleActivatesAt } as any)?.['futureDate']?.message || null;
  }

  get editSchedulePriceError(): string | null {
    if (!this.editSchedulePriceTouched) {
      return null;
    }
    return scheduledPriceValidator({ value: this.editSchedulePrice } as any)?.['scheduledPrice']?.message || null;
  }

  get editScheduleDateError(): string | null {
    if (!this.editScheduleDateTouched) {
      return null;
    }
    return futureDateTimeValidator({ value: this.editScheduleActivatesAt } as any)?.['futureDate']?.message || null;
  }

  get scheduleDeactivatesAtError(): string | null {
    if (!this.scheduleDeactivatesTouched) {
      return null;
    }
    return validateScheduleDeactivatesAt(this.scheduleDeactivatesAt, this.scheduleActivatesAt);
  }

  get editScheduleDeactivatesAtError(): string | null {
    if (!this.editScheduleDeactivatesTouched) {
      return null;
    }
    return validateScheduleDeactivatesAt(this.editScheduleDeactivatesAt, this.editScheduleActivatesAt);
  }

  get activeScheduleDeactivatesAtError(): string | null {
    if (!this.activeScheduleDeactivatesTouched) {
      return null;
    }
    const activatesLocal = this.activePriceSchedule?.activatesAt
      ? isoToDatetimeLocal(this.activePriceSchedule.activatesAt)
      : '';
    return validateScheduleDeactivatesAt(this.activeScheduleDeactivatesAt, activatesLocal);
  }

  get offerQtyError(): string | null {
    if (!this.offerQtyTouched) {
      return null;
    }
    return requiredQuantityValidator({ value: this.offerRequiredQuantity } as any)?.['requiredQuantity']?.message || null;
  }

  get offerPriceError(): string | null {
    if (!this.offerPriceTouched) {
      return null;
    }
    return offerPriceValidator({ value: this.offerPrice } as any)?.['offerPrice']?.message || null;
  }

  canSubmitSchedule(): boolean {
    return !scheduledPriceValidator({ value: this.schedulePrice } as any)
      && !futureDateTimeValidator({ value: this.scheduleActivatesAt } as any)
      && !validateScheduleDeactivatesAt(this.scheduleDeactivatesAt, this.scheduleActivatesAt);
  }

  canSubmitEditSchedule(): boolean {
    const isActiveOnly = this.upcomingPriceSchedule?.status === 'active';
    if (isActiveOnly) {
      return !validateScheduleDeactivatesAt(
        this.editScheduleDeactivatesAt,
        isoToDatetimeLocal(this.upcomingPriceSchedule?.activatesAt)
      );
    }
    return !scheduledPriceValidator({ value: this.editSchedulePrice } as any)
      && !futureDateTimeValidator({ value: this.editScheduleActivatesAt } as any)
      && !validateScheduleDeactivatesAt(this.editScheduleDeactivatesAt, this.editScheduleActivatesAt);
  }

  canSubmitActiveScheduleEnd(): boolean {
    const activatesLocal = this.activePriceSchedule?.activatesAt
      ? isoToDatetimeLocal(this.activePriceSchedule.activatesAt)
      : '';
    return !validateScheduleDeactivatesAt(this.activeScheduleDeactivatesAt, activatesLocal);
  }

  canSubmitOffer(): boolean {
    return !requiredQuantityValidator({ value: this.offerRequiredQuantity } as any)
      && !offerPriceValidator({ value: this.offerPrice } as any);
  }

  scheduleFuturePrice(): void {
    this.schedulePriceTouched = true;
    this.scheduleDateTouched = true;
    this.scheduleDeactivatesTouched = true;
    this.scheduleInlineError = '';

    if (!this.product?._id || !this.canSubmitSchedule()) {
      return;
    }

    const payload: any = {
      price: Number(this.schedulePrice),
      activatesAt: datetimeLocalToIso(this.scheduleActivatesAt)
    };
    if (this.scheduleDeactivatesAt) {
      payload.deactivatesAt = datetimeLocalToIso(this.scheduleDeactivatesAt);
    }

    this.scheduleSaving = true;
    this.ecommerceService.createProductPriceSchedule(this.storeId, this.product._id, payload).subscribe({
      next: () => {
        this.scheduleSaving = false;
        this.schedulePrice = null;
        this.scheduleActivatesAt = '';
        this.scheduleDeactivatesAt = '';
        this.schedulePriceTouched = false;
        this.scheduleDateTouched = false;
        this.scheduleDeactivatesTouched = false;
        this.schedulePanelOpen = false;
        this.toastr.success('Future price change scheduled');
        this.refreshPricingData();
      },
      error: (err: any) => {
        this.scheduleSaving = false;
        const message = err?.error?.response?.message || 'Failed to schedule price change';
        if (isDuplicateActivatesAtError(message)) {
          this.scheduleInlineError = 'A price change is already scheduled for this exact date and time. Choose a different activation time.';
        } else {
          this.scheduleInlineError = message;
        }
      }
    });
  }

  startEditUpcomingSchedule(): void {
    if (!this.upcomingPriceSchedule) {
      return;
    }
    this.editingUpcomingSchedule = true;
    this.editSchedulePrice = this.upcomingPriceSchedule.price;
    this.editScheduleActivatesAt = isoToDatetimeLocal(this.upcomingPriceSchedule.activatesAt);
    this.editScheduleDeactivatesAt = isoToDatetimeLocal(this.upcomingPriceSchedule.deactivatesAt);
    this.editSchedulePriceTouched = false;
    this.editScheduleDateTouched = false;
    this.editScheduleDeactivatesTouched = false;
    this.scheduleInlineError = '';
  }

  cancelEditUpcomingSchedule(): void {
    this.editingUpcomingSchedule = false;
    this.editSchedulePrice = null;
    this.editScheduleActivatesAt = '';
    this.scheduleInlineError = '';
  }

  saveUpcomingScheduleEdit(): void {
    this.editSchedulePriceTouched = true;
    this.editScheduleDateTouched = true;
    this.editScheduleDeactivatesTouched = true;
    this.scheduleInlineError = '';

    if (!this.product?._id || !this.upcomingPriceSchedule?._id || !this.canSubmitEditSchedule()) {
      return;
    }

    const isActiveOnly = this.upcomingPriceSchedule.status === 'active';
    const payload: any = isActiveOnly
      ? {
        deactivatesAt: this.editScheduleDeactivatesAt
          ? datetimeLocalToIso(this.editScheduleDeactivatesAt)
          : null
      }
      : {
        price: Number(this.editSchedulePrice),
        activatesAt: datetimeLocalToIso(this.editScheduleActivatesAt),
        ...(this.editScheduleDeactivatesAt
          ? { deactivatesAt: datetimeLocalToIso(this.editScheduleDeactivatesAt) }
          : { deactivatesAt: null })
      };

    this.scheduleSaving = true;
    this.ecommerceService.updateProductPriceSchedule(
      this.storeId,
      this.product._id,
      this.upcomingPriceSchedule._id,
      payload
    ).subscribe({
      next: () => {
        this.scheduleSaving = false;
        this.editingUpcomingSchedule = false;
        this.toastr.success('Scheduled price updated');
        this.refreshPricingData();
      },
      error: (err: any) => {
        this.scheduleSaving = false;
        const message = err?.error?.response?.message || 'Failed to update scheduled price';
        if (isDuplicateActivatesAtError(message)) {
          this.scheduleInlineError = 'A price change is already scheduled for this exact date and time. Choose a different activation time.';
        } else {
          this.scheduleInlineError = message;
        }
      }
    });
  }

  cancelUpcomingSchedule(): void {
    if (!this.product?._id || !this.upcomingPriceSchedule?._id) {
      return;
    }

    Swal.fire({
      title: 'Cancel scheduled price?',
      text: 'The upcoming price change will be cancelled.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, cancel it'
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }

      this.scheduleSaving = true;
      this.ecommerceService.deleteProductPriceSchedule(
        this.storeId,
        this.product._id,
        this.upcomingPriceSchedule._id
      ).subscribe({
        next: () => {
          this.scheduleSaving = false;
          this.editingUpcomingSchedule = false;
          this.toastr.success('Scheduled price cancelled');
          this.refreshPricingData();
        },
        error: (err: any) => {
          this.scheduleSaving = false;
          this.toastr.error(err?.error?.response?.message || 'Failed to cancel scheduled price');
        }
      });
    });
  }

  formatScheduleDate(value: string): string {
    return formatPricingDate(value);
  }

  startEditActiveScheduleEnd(): void {
    if (!this.activePriceSchedule) {
      return;
    }
    this.editingActiveScheduleEnd = true;
    this.activeScheduleDeactivatesAt = isoToDatetimeLocal(this.activePriceSchedule.deactivatesAt);
    this.activeScheduleDeactivatesTouched = false;
    this.activeScheduleEndError = '';
  }

  cancelEditActiveScheduleEnd(): void {
    this.editingActiveScheduleEnd = false;
    this.activeScheduleDeactivatesAt = '';
    this.activeScheduleEndError = '';
  }

  saveActiveScheduleEnd(): void {
    this.activeScheduleDeactivatesTouched = true;
    this.activeScheduleEndError = this.activeScheduleDeactivatesAtError || '';
    if (!this.product?._id || !this.activePriceSchedule?._id || !this.canSubmitActiveScheduleEnd()) {
      return;
    }

    this.scheduleSaving = true;
    this.ecommerceService.updateProductPriceSchedule(this.storeId, this.product._id, this.activePriceSchedule._id, {
      deactivatesAt: this.activeScheduleDeactivatesAt
        ? datetimeLocalToIso(this.activeScheduleDeactivatesAt)
        : null
    }).subscribe({
      next: () => {
        this.scheduleSaving = false;
        this.editingActiveScheduleEnd = false;
        this.toastr.success('Active price end updated');
        this.refreshPricingData();
      },
      error: (err: any) => {
        this.scheduleSaving = false;
        this.toastr.error(err?.error?.response?.message || 'Failed to update end date');
      }
    });
  }

  clearActiveScheduleEnd(): void {
    if (!this.product?._id || !this.activePriceSchedule?._id) {
      return;
    }
    this.scheduleSaving = true;
    this.ecommerceService.updateProductPriceSchedule(this.storeId, this.product._id, this.activePriceSchedule._id, {
      deactivatesAt: null
    }).subscribe({
      next: () => {
        this.scheduleSaving = false;
        this.editingActiveScheduleEnd = false;
        this.toastr.success('Scheduled end cleared');
        this.refreshPricingData();
      },
      error: (err: any) => {
        this.scheduleSaving = false;
        this.toastr.error(err?.error?.response?.message || 'Failed to clear end date');
      }
    });
  }

  openAddOfferForm(): void {
    this.resetOfferForm();
    this.showOfferForm = true;
  }

  startEditOffer(offer: any): void {
    this.editingOfferId = offer._id;
    this.offerRequiredQuantity = offer.requiredQuantity;
    this.offerPrice = offer.offerPrice;
    this.offerValidFrom = isoToDatetimeLocal(offer.validFrom);
    this.offerValidTo = isoToDatetimeLocal(offer.validTo);
    this.offerQtyTouched = false;
    this.offerPriceTouched = false;
    this.offerInlineError = '';
    this.showOfferForm = true;
  }

  cancelOfferForm(): void {
    this.resetOfferForm();
    this.showOfferForm = false;
  }

  private resetOfferForm(): void {
    this.editingOfferId = null;
    this.offerRequiredQuantity = null;
    this.offerPrice = null;
    this.offerValidFrom = '';
    this.offerValidTo = '';
    this.offerQtyTouched = false;
    this.offerPriceTouched = false;
    this.offerInlineError = '';
  }

  saveOffer(): void {
    this.offerQtyTouched = true;
    this.offerPriceTouched = true;
    this.offerInlineError = '';

    if (!this.product?._id || !this.canSubmitOffer()) {
      return;
    }

    const payload: any = {
      requiredQuantity: Number(this.offerRequiredQuantity),
      offerPrice: Number(this.offerPrice)
    };
    const validFromIso = datetimeLocalToIso(this.offerValidFrom);
    const validToIso = datetimeLocalToIso(this.offerValidTo);
    if (validFromIso) {
      payload.validFrom = validFromIso;
    }
    if (validToIso) {
      payload.validTo = validToIso;
    }

    this.offerSaving = true;
    const request$ = this.editingOfferId
      ? this.ecommerceService.updateProductMultiBuyOffer(this.storeId, this.product._id, this.editingOfferId, payload)
      : this.ecommerceService.createProductMultiBuyOffer(this.storeId, this.product._id, payload);

    request$.subscribe({
      next: () => {
        this.offerSaving = false;
        this.toastr.success(this.editingOfferId ? 'Offer updated' : 'Offer added');
        this.showOfferForm = false;
        this.resetOfferForm();
        this.loadMultiBuyOffers();
      },
      error: (err: any) => {
        this.offerSaving = false;
        this.offerInlineError = err?.error?.response?.message || 'Failed to save offer';
      }
    });
  }

  toggleOfferActive(offer: any): void {
    if (!this.product?._id || !offer?._id) {
      return;
    }

    const nextActive = offer.isActive === false;
    this.ecommerceService.updateProductMultiBuyOffer(
      this.storeId,
      this.product._id,
      offer._id,
      { isActive: nextActive }
    ).subscribe({
      next: () => {
        this.toastr.success(nextActive ? 'Offer enabled' : 'Offer disabled');
        this.loadMultiBuyOffers();
      },
      error: (err: any) => {
        this.toastr.error(err?.error?.response?.message || 'Failed to update offer');
      }
    });
  }

  deleteOffer(offer: any): void {
    if (!this.product?._id || !offer?._id) {
      return;
    }

    Swal.fire({
      title: 'Delete offer?',
      text: formatMultiBuyOfferSummary(offer, this.storeCurrency),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete'
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }

      this.ecommerceService.deleteProductMultiBuyOffer(this.storeId, this.product._id, offer._id).subscribe({
        next: () => {
          this.toastr.success('Offer deleted');
          this.loadMultiBuyOffers();
        },
        error: (err: any) => {
          this.toastr.error(err?.error?.response?.message || 'Failed to delete offer');
        }
      });
    });
  }

  getOfferStatus(offer: any): MultiBuyOfferStatusInfo {
    return getMultiBuyOfferStatus(offer);
  }

  formatOfferSummary(offer: any): string {
    return formatMultiBuyOfferSummary(offer, this.storeCurrency);
  }

  formatOfferDate(value: string): string {
    return formatPricingDate(value);
  }

  get productDiscountGroupLabel(): string {
    return (this.productForm.get('discountGroup')?.value || '').trim();
  }

  get primaryActiveDiscountGroupOffer(): any | null {
    const active = this.discountGroupOffersForProduct.filter((o) => o.isCurrentlyActive);
    if (!active.length) {
      return null;
    }
    return active.sort(
      (a, b) => (Number(b.requiredQuantity) || 0) - (Number(a.requiredQuantity) || 0)
    )[0];
  }

  private loadDiscountGroupOffersForProduct(): void {
    if (!this.product?._id || !this.storeId) {
      this.discountGroupOffersForProduct = [];
      return;
    }

    const group = this.productDiscountGroupLabel;
    if (!group) {
      this.discountGroupOffersForProduct = [];
      return;
    }

    this.discountGroupOffersLoading = true;
    this.ecommerceService.getDiscountGroupOffers(this.storeId, { discountGroup: group }).subscribe({
      next: (res: any) => {
        const data = res?.response?.data ?? res?.data;
        this.discountGroupOffersForProduct = Array.isArray(data)
          ? data
          : data?.items || [];
        this.discountGroupOffersLoading = false;
      },
      error: () => {
        this.discountGroupOffersLoading = false;
        this.discountGroupOffersForProduct = [];
      },
    });
  }

  private extractPricingDetails(data: any): void {
    if (!data) {
      return;
    }
    const product = data.product ?? data;
    const upcoming = data.upcomingPriceSchedule ?? product?.upcomingPriceSchedule ?? null;
    if (upcoming?.status === 'active') {
      this.activePriceSchedule = upcoming;
      this.upcomingPriceSchedule = null;
    } else {
      this.upcomingPriceSchedule = upcoming;
    }
    if (Array.isArray(product?.multiBuyOffers)) {
      this.multiBuyOffers = product.multiBuyOffers;
    }
  }

  private syncSchedulesFromHistory(schedules: any[]): void {
    if (!Array.isArray(schedules)) {
      return;
    }
    const active = schedules.find((s) => s.status === 'active');
    this.activePriceSchedule = active || null;
    if (!active) {
      this.editingActiveScheduleEnd = false;
      this.activeScheduleDeactivatesAt = '';
      this.activeScheduleDeactivatesTouched = false;
      this.activeScheduleEndError = '';
    }
    if (!this.upcomingPriceSchedule) {
      const scheduled = schedules.find((s) => s.status === 'scheduled');
      if (scheduled) {
        this.upcomingPriceSchedule = scheduled;
      }
    }
  }

  private loadPricingData(): void {
    if (!this.product?._id) {
      return;
    }

    this.extractPricingDetails(this.product);

    this.ecommerceService.getProductDetails(this.storeId, this.product._id).subscribe({
      next: (res: any) => {
        this.extractPricingDetails(res?.response?.data ?? res?.data);
      },
      error: () => { /* keep initial product data */ }
    });

    this.loadMultiBuyOffers();
    this.loadDiscountGroupOffersForProduct();
    this.loadPriceScheduleHistory();
  }

  private loadPriceScheduleHistory(): void {
    if (!this.product?._id) {
      return;
    }
    this.ecommerceService.getProductPriceScheduleHistory(this.storeId, this.product._id).subscribe({
      next: (res: any) => {
        const data = res?.response?.data ?? res?.data;
        const schedules = Array.isArray(data)
          ? data
          : (data?.priceSchedule || data?.schedules || []);
        this.syncSchedulesFromHistory(schedules);
      },
      error: () => { /* ignore */ }
    });
  }

  private refreshPricingData(): void {
    if (!this.product?._id) {
      return;
    }

    this.ecommerceService.getProductDetails(this.storeId, this.product._id).subscribe({
      next: (res: any) => {
        this.extractPricingDetails(res?.response?.data ?? res?.data);
      },
      error: () => { /* ignore */ }
    });

    this.loadMultiBuyOffers();
    this.loadPriceScheduleHistory();
  }

  private loadMultiBuyOffers(): void {
    if (!this.product?._id) {
      return;
    }

    this.offersLoading = true;
    this.ecommerceService.getProductMultiBuyOffers(this.storeId, this.product._id).subscribe({
      next: (res: any) => {
        const data = res?.response?.data ?? res?.data;
        this.multiBuyOffers = Array.isArray(data)
          ? data
          : (data?.multiBuyOffers || data?.offers || []);
        this.offersLoading = false;
      },
      error: (err: any) => {
        this.offersLoading = false;
        this.toastr.error(err?.error?.response?.message || 'Failed to load multi-buy offers');
      }
    });
  }

  private loadCurrentPlacement(): void {
    if (!this.product?._id) {
      return;
    }

    if (this.product.currentPlacement) {
      patchPlacement(this.productForm, this.product.currentPlacement);
      this.savedPlacementSnapshot = snapshotPlacementValues(this.productForm.value);
    }

    this.ecommerceService.getProductDetails(this.storeId, this.product._id).subscribe({
      next: (res: any) => {
        const data = res?.response?.data ?? res?.data;
        if (data?.currentPlacement) {
          patchPlacement(this.productForm, data.currentPlacement);
          this.savedPlacementSnapshot = snapshotPlacementValues(this.productForm.value);
          return;
        }
        this.loadActivePlacementFromHistory();
      },
      error: () => {
        if (!this.savedPlacementSnapshot) {
          this.savedPlacementSnapshot = snapshotPlacementValues(this.productForm.value);
        }
      }
    });
  }

  private loadActivePlacementFromHistory(): void {
    if (!this.product?._id) {
      return;
    }
    this.ecommerceService.getProductPlacementHistory(this.storeId, this.product._id).subscribe({
      next: (res: any) => {
        const data = res?.response?.data ?? res?.data;
        const placements = Array.isArray(data) ? data : (data?.placements || []);
        const activePlacement = placements.find((p: any) => p?.isActive) || placements[0];
        if (activePlacement) {
          patchPlacement(this.productForm, activePlacement);
        }
        this.savedPlacementSnapshot = snapshotPlacementValues(this.productForm.value);
      },
      error: () => {
        this.savedPlacementSnapshot = snapshotPlacementValues(this.productForm.value);
      }
    });
  }

  private shouldSavePlacementOnCreate(): boolean {
    return hasPlacementInput(snapshotPlacementValues(this.productForm.value));
  }

  private hasPlacementChanged(): boolean {
    const current = snapshotPlacementValues(this.productForm.value);
    return !placementValuesEqual(current, this.savedPlacementSnapshot);
  }

  private saveProductPlacement(productId: string) {
    return this.ecommerceService.setProductPlacement(
      this.storeId,
      productId,
      buildPlacementPayload(this.productForm.value)
    );
  }
}
